'use client'

import { Box, UnstyledButton } from '@mantine/core'
import clsx from 'clsx'

import SquareGrid2x2FillIcon from '@/assets/icons/square-grid-2x2-fill.svg'
import NextBaseImage from '@/components/basic/BaseImage'
import type { PredictionNavNode } from '@/services/prediction/types'

import itemStyles from '../NavTree/items.module.css'
import styles from './styles.module.css'

interface LiveEventKindSidebarProps {
  /** 当前选中节点；null = All（未选任何子分类）。 */
  selectedNode: PredictionNavNode | null
  /** All 行计数：live 子树 event_count 之和（BE 快照，见调用方）。 */
  totalCount: number | undefined
  /** curated live 树的子分类节点（BE 原序）。 */
  nodes: PredictionNavNode[]
  onSelect: (node: PredictionNavNode | null) => void
}

/**
 * `/price` Live L1 的桌面左侧栏（cdc-web `LiveEventKindSidebar` 的 cdc-market
 * 移植）。
 *
 * 与 NavTreeSidebar 的关键区别：live 子分类是**扁平**的，不做树。
 * curated live 树里 crypto / financials 虽然 `node_type` 是 folder 且带
 * children（per-token 叶子，见 `/navigation-tree/live`），但 cdc-web 的 live
 * 侧栏只铺 BE 的一层节点、忽略 children —— 所以既没有展开 chevron，也没有
 * 分隔线，选中即整行高亮并重查 feed。此前这里误用了通用
 * NavTreeSidebar，于是 Live 的左栏长得像分类页：可展开、带分割线、且点
 * Crypto 会先落到 folder 的全集过滤。
 *
 * 复用 NavTree 的 `items.module.css` 行样式（cdc-web 同款做法），保证两个
 * 侧栏的排版节奏一致，但不引入其树/手风琴/递归行为。
 */
export default function LiveEventKindSidebar({
  selectedNode,
  totalCount,
  nodes,
  onSelect,
}: Readonly<LiveEventKindSidebarProps>) {
  return (
    <Box component="aside" className={styles.sidebar}>
      <Box component="ul" className={clsx(itemStyles.list, styles.list)}>
        <SidebarRow
          label="All"
          isAllRow
          count={totalCount}
          active={selectedNode === null}
          onClick={() => onSelect(null)}
        />
        {nodes.map(node => (
          <SidebarRow
            key={node.id}
            label={node.display_name}
            icon={node.icon_url || undefined}
            count={node.event_count}
            active={selectedNode?.id === node.id}
            onClick={() => onSelect(node)}
          />
        ))}
      </Box>
    </Box>
  )
}

interface SidebarRowProps {
  label: string
  icon?: string
  isAllRow?: boolean
  count: number | undefined
  active: boolean
  onClick: () => void
}

/** live 侧栏的单行；All 行走 2×2 grid 图标，其余走 BE `icon_url`。 */
function SidebarRow({ label, icon, isAllRow, count, active, onClick }: Readonly<SidebarRowProps>) {
  return (
    <Box component="li" className={itemStyles.item}>
      <UnstyledButton
        className={clsx(itemStyles.row, itemStyles.destinationRow, active && itemStyles.rowActive)}
        onClick={onClick}
        aria-pressed={active}
      >
        <span className={itemStyles.label}>
          {icon ? (
            <NextBaseImage
              src={icon}
              alt=""
              aria-hidden="true"
              width={16}
              height={16}
              unoptimized
              className={clsx(itemStyles.icon, styles.icon)}
              fallback={<span aria-hidden="true" className={itemStyles.icon} />}
            />
          ) : isAllRow ? (
            <SquareGrid2x2FillIcon
              width={16}
              height={16}
              aria-hidden="true"
              className={clsx(itemStyles.icon, itemStyles.localIcon, itemStyles.allIcon)}
            />
          ) : (
            <span aria-hidden="true" className={itemStyles.icon} />
          )}
          <span>{label}</span>
        </span>
        {/* count 缺失时不渲染 "(0)" —— 树未就绪（首载中）传 undefined，
            RowCount 在 NavTree 里同一套语义。 */}
        {typeof count === 'number' && <span className={itemStyles.count}>({count})</span>}
      </UnstyledButton>
    </Box>
  )
}
